import React, { useEffect, useState } from 'react';

/**
 * AddToHomeScreen — the daily-habit hook. Mobile only, shown after locking
 * (the "come back tomorrow" moment). Android gets the native install prompt
 * via beforeinstallprompt; iOS has no API, so the button unfolds the
 * Share → Add to Home Screen walkthrough. Installed app opens /curve?src=a2hs
 * (set in manifest.json), so PWA opens show up in analytics page views.
 */

const DISMISS_KEY = 'ftA2hsDismissedAt';
const DISMISS_DAYS = 30;

// Chrome fires beforeinstallprompt early — capture at module load so the
// event isn't missed while React mounts.
let deferredPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}

const isStandalone = () =>
  (typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(display-mode: standalone)').matches) ||
  (typeof navigator !== 'undefined' && navigator.standalone === true);

const isIOS = () =>
  typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

const recentlyDismissed = () => {
  try {
    const at = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return at && Date.now() - at < DISMISS_DAYS * 86400000;
  } catch (e) {
    return false;
  }
};

const styles = `
  .cva2hs { position: relative; background: #FFFDF8; border: 1px solid #EFE4D2; border-radius: 16px;
    padding: 18px 18px 16px; margin-top: 12px; }
  .cva2hs-t { font-size: 17px; font-weight: 700; letter-spacing: -.01em; color: #1D1D1F; }
  .cva2hs-s { font-size: 13px; color: #6E6E73; line-height: 1.5; margin: 5px 0 13px; }
  .cva2hs .cva2hs-btn { width: 100%; background: #000; color: #fff; border: none; cursor: pointer;
    border-radius: 980px; padding: 14px; font: inherit; font-size: 15px; font-weight: 600; min-height: 48px; }
  .cva2hs .cva2hs-x { position: absolute; top: 8px; right: 8px; width: 34px; height: 34px; border: none;
    background: none; cursor: pointer; color: #86868B; font-size: 16px; border-radius: 50%; }
  .cva2hs .cva2hs-x:hover { background: rgba(0,0,0,.05); }
  .cva2hs-how { margin: 4px 0 0; }
  .cva2hs-step { display: flex; gap: 10px; align-items: baseline; font-size: 13.5px; color: #1D1D1F;
    line-height: 1.5; margin-top: 9px; }
  .cva2hs-step i { font-style: normal; font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    font-size: 10px; font-weight: 700; color: #C25E00; flex: none; }
  .cva2hs-step svg { width: 15px; height: 15px; vertical-align: -2px; margin: 0 1px; }
`;

const ShareGlyph = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 12.5V3.5" />
    <path d="m6.8 6.2 3.2-3.2 3.2 3.2" />
    <path d="M5 9.5H4.5A1.5 1.5 0 0 0 3 11v4.5A1.5 1.5 0 0 0 4.5 17h11a1.5 1.5 0 0 0 1.5-1.5V11a1.5 1.5 0 0 0-1.5-1.5H15" />
  </svg>
);

export default function AddToHomeScreen() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  const [hidden, setHidden] = useState(() => isStandalone() || recentlyDismissed());
  const [showHow, setShowHow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, []);

  if (!isMobile || hidden) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      /* noop */
    }
    setHidden(true);
  };

  const add = async () => {
    if (deferredPrompt) {
      const prompt = deferredPrompt;
      deferredPrompt = null;
      prompt.prompt();
      try {
        const { outcome } = await prompt.userChoice;
        if (outcome === 'accepted') {
          setHidden(true);
          return;
        }
      } catch (e) {
        /* fall through to instructions */
      }
    }
    setShowHow(true);
  };

  return (
    <div className="cva2hs">
      <style>{styles}</style>
      <button type="button" className="cva2hs-x" onClick={dismiss} aria-label="Dismiss">✕</button>
      <div className="cva2hs-t">Play every morning.</div>
      <div className="cva2hs-s">
        Put The Curve on your Home Screen — tomorrow's player is one tap away.
      </div>
      {!showHow ? (
        <button type="button" className="cva2hs-btn" onClick={add}>
          Add to Home Screen
        </button>
      ) : (
        <div className="cva2hs-how">
          {isIOS() ? (
            <>
              <div className="cva2hs-step"><i>01</i><span>Tap the <ShareGlyph /> Share button in Safari's toolbar.</span></div>
              <div className="cva2hs-step"><i>02</i><span>Scroll down and tap "Add to Home Screen" — it can hide under "View More."</span></div>
              <div className="cva2hs-step"><i>03</i><span>Tap Add — that's it.</span></div>
            </>
          ) : (
            <>
              <div className="cva2hs-step"><i>01</i><span>Open your browser's menu (the three dots).</span></div>
              <div className="cva2hs-step"><i>02</i><span>Tap "Add to Home screen," then Add.</span></div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
